import React, { useCallback, useEffect, useState } from "react";
import { Eye, MoreVertical, Plus } from "lucide-react";
import {
  getPatientDocuments,
  deletePatientDocument,
  viewPatientDocument,
} from "../APIs/patientDocumentApi";
import PatientDocumentModal from "./PatientDocumentModal";

const UPLOADABLE_TYPES = [
  { value: "GOVERNMENT_ID", label: "Government ID" },
  { value: "INTAKE_FORM", label: "Intake Form" },
  { value: "CLINICAL_DOCUMENT", label: "Clinical Supporting Document" },
];

const TYPE_LABEL = {
  GOVERNMENT_ID: "Government ID",
  INTAKE_FORM: "Intake Form",
  CLINICAL_DOCUMENT: "Clinical Supporting Document",
  REFERRAL: "Referral",
};

const STATUS_CONFIG = {
  VERIFIED: { label: "Verified", color: "bg-green-100 text-green-700" },
  PENDING_REVIEW: { label: "Pending", color: "bg-gray-100 text-gray-500" },
};

function fmt(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export default function PatientDocumentsTab({ patientId }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | { mode: 'add' } | { mode: 'edit', doc }
  const [menuOpen, setMenuOpen] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState({});
  const [viewing, setViewing] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPatientDocuments(patientId);
      setDocs(res);
    } catch {
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleView = async (doc) => {
    setViewing((v) => ({ ...v, [doc.documentId]: true }));
    try {
      const url = await viewPatientDocument(doc.documentId);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      alert("Unable to load document. Please try again.");
    } finally {
      setViewing((v) => ({ ...v, [doc.documentId]: false }));
    }
  };

  const handleDelete = async (doc) => {
    setDeleting((d) => ({ ...d, [doc.documentId]: true }));
    try {
      await deletePatientDocument(doc.documentId, patientId);
      setConfirmDelete(null);
      await load();
    } catch (err) {
      alert(err?.response?.data?.message || "Delete failed");
    } finally {
      setDeleting((d) => ({ ...d, [doc.documentId]: false }));
    }
  };

  const usedTypes = docs
    .filter((d) => UPLOADABLE_TYPES.some((t) => t.value === d.documentType))
    .map((d) => d.documentType);

  const isReferral = (doc) => doc.documentType === "REFERRAL";

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Documents</h2>
          <p className="text-sm text-gray-500">
            Your uploaded documents and referrals.
          </p>
        </div>
        <button
          onClick={() => setModal({ mode: "add" })}
          className="flex items-center gap-1.5 px-4 py-2 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700"
        >
          <Plus className="w-4 h-4" />
          Add Document
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border">
        {/* Header */}
        <div className="grid grid-cols-[2fr_160px_140px_120px_80px] px-4 py-2.5 bg-gray-50 border-b text-xs font-semibold text-gray-500 uppercase tracking-wide">
          <span>Document</span>
          <span>Type</span>
          <span>Expiration Date</span>
          <span>Status</span>
          <span>Actions</span>
        </div>

        {loading && (
          <p className="text-sm text-gray-400 text-center py-8">
            Loading documents...
          </p>
        )}
        {!loading && docs.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-8">
            No documents yet.
          </p>
        )}

        {!loading &&
          docs.map((doc, i) => {
            const statusCfg =
              STATUS_CONFIG[doc.status] ?? STATUS_CONFIG.PENDING_REVIEW;
            const isConfirming = confirmDelete === doc.documentId;
            const referral = isReferral(doc);

            return (
              <div
                key={doc.documentId}
                className={`grid grid-cols-[2fr_160px_140px_120px_80px] items-center px-4 py-3 text-sm ${
                  i < docs.length - 1 ? "border-b border-gray-100" : ""
                }`}
              >
                <span className="font-medium text-gray-900 truncate pr-2">
                  {doc.fileName || doc.documentType}
                </span>
                <span className="text-gray-500 text-xs">
                  {TYPE_LABEL[doc.documentType] ?? doc.documentType}
                </span>
                <span className="text-gray-500 text-xs">
                  {fmt(doc.expiresAt)}
                </span>
                <span
                  className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium w-fit ${statusCfg.color}`}
                >
                  {statusCfg.label}
                </span>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  {isConfirming ? (
                    <div className="flex items-center gap-1">
                      <button
                        disabled={deleting[doc.documentId]}
                        onClick={() => handleDelete(doc)}
                        className="text-xs px-2 py-1 rounded bg-red-600 text-white disabled:opacity-40"
                      >
                        {deleting[doc.documentId] ? "..." : "Confirm"}
                      </button>
                      <button
                        onClick={() => setConfirmDelete(null)}
                        className="text-xs px-2 py-1 rounded border"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <>
                      <button
                        onClick={() => handleView(doc)}
                        disabled={viewing[doc.documentId]}
                        className="text-gray-400 hover:text-teal-600 transition disabled:opacity-40"
                        title="View"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {!referral && (
                        <div className="relative">
                          <button
                            onClick={() =>
                              setMenuOpen(
                                menuOpen === doc.documentId
                                  ? null
                                  : doc.documentId,
                              )
                            }
                            className="text-gray-400 hover:text-gray-600 transition"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>
                          {menuOpen === doc.documentId && (
                            <div className="absolute right-0 top-6 z-50 bg-white border rounded-lg shadow-lg py-1 w-28">
                              <button
                                onClick={() => {
                                  setMenuOpen(null);
                                  setModal({ mode: "edit", doc });
                                }}
                                className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => {
                                  setMenuOpen(null);
                                  setConfirmDelete(doc.documentId);
                                }}
                                className="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50"
                              >
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
      </div>

      {/* Modal */}
      {modal && (
        <PatientDocumentModal
          patientId={patientId}
          usedTypes={usedTypes}
          document={modal.doc ?? null}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            load();
          }}
        />
      )}

      {/* Close menu on outside click */}
      {menuOpen && (
        <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(null)} />
      )}
    </div>
  );
}
